import { useAppRouter } from "@/utils/router";
import { useCallback, useEffect, useRef } from "react";

/**
 * 推送通知工具函数
 * 处理 WebView 推送消息跳转，支持冷启动和热启动场景
 */

declare global {
    interface Window {
        onNotification: ( data: NotificationData ) => void;
    }

    interface WindowEventMap {
        notification: CustomEvent<NotificationData>;
    }
}

type NotificationData = {
    eventNo: string;
    eventCategory: string;
    messageType: string;
};
/**
 * 从 URL 中获取指定查询参数
 * @param name - 参数名
 * @returns 参数值，如果未找到返回 null
 * @example
 * // URL: https://example.com?push_redirect=/order/123
 * getQueryParam('push_redirect') // returns '/order/123'
 */
export function getQueryParam( name: string ): string | null {
    // 方式 1: 现代浏览器/WebView (推荐)
    if ( typeof window !== "undefined" && window.URLSearchParams ) {
        const params = new URLSearchParams( window.location.search );
        return params.get( name );
    }

    // 方式 2: 正则表达式 (兜底方案，兼容极旧的 Android WebView)
    if ( typeof window !== "undefined" ) {
        const reg = new RegExp( "(^|&)" + name + "=([^&]*)(&|$)" );
        const r = window.location.search.substring( 1 ).match( reg );
        if ( r != null ) return decodeURIComponent( r[2] );
    }

    return null;
}

function safeParse<T>( json: string | null, fallback: T ): T {
    if ( !json ) return fallback;
    try {
        return JSON.parse( json );
    } catch {
        return fallback;
    }
}

export function useNotification() {
    const router = useAppRouter();
    const hasRedirected = useRef( false );

    const handleNotification = useCallback(
        ( event: WindowEventMap["notification"] ) => {
            const data = event.detail;
            // 根据 eventNO、eventCategory 和 messageType 进行相应的处理
            // 例如：根据 eventCategory 跳转到不同的页面
            if ( data.messageType && data.messageType === "event" && data.eventNo ) {
                if ( [ "1", "2", "3" ].includes( data.eventCategory ) ) {
                    const path = `/sport-event-detail?eventno=${data.eventNo}`;
                    router.push( path );
                } else if ( data.eventCategory == "0" ) {
                    const path = `/main-event-detail?eventno=${data.eventNo}`;
                    router.push( path );
                }
            }
        },
        [ router ],
    );

    const handleRedirect = useCallback( async () => {
        if ( hasRedirected.current ) return;
        const redirectTarget = getQueryParam( "redirect_url" );

        if ( redirectTarget ) {
            hasRedirected.current = true;
            // 使用 Next.js router.replace 清除 redirect_url 参数，保留其他参数
            // 相比 window.history.replaceState，replace 会同步更新 Next.js 内部路由状态，
            // 不会被 Next.js 后续的 replaceState 覆盖
            await Promise.resolve();
            const params = new URLSearchParams( window.location.search );
            params.delete( "redirect_url" );
            const search = params.toString();
            router.replace( window.location.pathname + ( search ? `?${search}` : "" ) );

            if ( redirectTarget.startsWith( "/push" ) ) {
                // 将redirectTarget上的参数提取出来，然后调用handleNotification方法
                const url = new URL( redirectTarget, window.location.origin );
                const data: NotificationData = safeParse<NotificationData>(
                    url.searchParams.get( "data" ),
                    {} as NotificationData,
                );
                await new Promise( ( resolve ) => setTimeout( resolve, 200 ) ); // 等待200ms，确保页面加载完成
                handleNotification( new CustomEvent( "notification", { detail: data } ) );
            } else if ( redirectTarget.startsWith( "http" ) ) {
                window.location.href = redirectTarget;
            } else if ( redirectTarget.startsWith( "/" ) ) {
                router.push( redirectTarget );
            }
        }
    }, [ handleNotification, router ] );

    useEffect( () => {
        handleRedirect();
        window.addEventListener( "notification", handleNotification );

        return () => {
            window.removeEventListener( "notification", handleNotification );
        };
    }, [ handleNotification, handleRedirect ] );
}

// eventCategory 1 2 3 是体育类事件。其余是普通事件。messageType是event代表跳转事件详情
export function onNotification( data: NotificationData | string ) {
    // 兼容 Android JSBridge 传对象或字符串两种情况
    const parsedData = typeof data === "string" ? safeParse<NotificationData>( data, {} as NotificationData ) : data;
    window.dispatchEvent( new CustomEvent( "notification", { detail: parsedData } ) );
}

// 模块初始化时注册全局方法，守卫避免 SSR/SSG 报错
if ( typeof window !== "undefined" ) {
    window.onNotification = onNotification;
}

/**
 * 热启动时：android通过jsbridge调用window.onNotification传递消息
 * window.onNotification({eventNo:'EV1767077643883584207',eventCategory:'1',messageType:'event'})
 *
 * 冷启动时：通过url参数传递消息，参数名为redirect_url，格式为：/push?data={}
 * http://localhost:3000/?redirect_url=/push?data={%22eventNo%22:%22EV1767077643883584207%22,%22eventCategory%22:%221%22,%22messageType%22:%22event%22}
 */
